import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CoachLayout } from '@/components/coach/layout/CoachLayout';
import { sportConfig } from '@/lib/coachConstants';
import type { SportType } from '@/lib/coachConstants';
import { usePlanInstances, usePlanCalendar, useAddCalendarEntry, useDeleteCalendarEntry, useUpdateCalendarEntry } from '@/hooks/usePlanInstances';
import { usePlanTemplate } from '@/hooks/usePlanTemplates';
import { useWorkoutTemplates } from '@/hooks/useWorkoutTemplates';
import { useCoachStore } from '@/stores/coachStore';
import type { CalendarEntryResponse } from '@/lib/templateApi';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent,
  DragOverlay, useDroppable, useDraggable,
} from '@dnd-kit/core';

// ── Week navigation helpers ──
function getWeekDates(baseDate: Date): string[] {
  const monday = new Date(baseDate);
  const day = monday.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  monday.setDate(monday.getDate() + diff);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d.toISOString().split('T')[0];
  });
}

function formatWeekLabel(dates: string[]): string {
  const start = new Date(dates[0]);
  const end = new Date(dates[6]);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[start.getMonth()]} ${start.getDate()} – ${months[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
}

const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const today = new Date().toISOString().split('T')[0];

// ── Resolved workout for display ──
interface ResolvedWorkout {
  id: string;
  workout_template_id: string;
  name: string;
  sport: SportType;
  stimulus: string | null;
}

function DraggableWorkout({ workout, onRemove }: { workout: ResolvedWorkout; onRemove: () => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: workout.id });
  const sport = sportConfig[workout.sport] ?? sportConfig.running;

  return (
    <div ref={setNodeRef} {...attributes} {...listeners}
      className={cn('rounded-md border px-2.5 py-2 cursor-grab active:cursor-grabbing transition-all text-left group/workout', isDragging ? 'opacity-40' : 'hover:border-accent/30')}
      style={{ borderColor: `${sport.color}25`, background: `${sport.color}08` }}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-medium text-foreground truncate">{workout.name}</p>
          <div className="flex items-center gap-1.5 mt-1">
            <div className="h-1.5 w-1.5 rounded-full" style={{ background: sport.color }} />
            <span className="text-[9px] text-muted-foreground">{sport.label}</span>
            {workout.stimulus && <span className="text-[9px] text-muted-foreground">· {workout.stimulus}</span>}
          </div>
        </div>
        <button onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="opacity-0 group-hover/workout:opacity-100 transition-opacity text-muted-foreground hover:text-destructive p-0.5"
          onPointerDown={(e) => e.stopPropagation()}>
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

function DroppableDay({ date, dayName, workouts, onRemoveWorkout, onAddWorkout }: {
  date: string; dayName: string; workouts: ResolvedWorkout[]; onRemoveWorkout: (id: string) => void; onAddWorkout: () => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: date });
  const isToday = date === today;
  const dayNum = parseInt(date.split('-')[2], 10);

  return (
    <div ref={setNodeRef}
      className={cn('rounded-lg border min-h-[140px] p-2 transition-all flex flex-col group',
        isOver ? 'border-accent/50 bg-accent/5' : 'border-border/15 bg-card/20',
        isToday && 'ring-1 ring-accent/30')}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{dayName}</span>
          <span className={cn('text-[10px] font-mono', isToday ? 'bg-accent text-accent-foreground px-1.5 py-0.5 rounded' : 'text-muted-foreground')}>
            {dayNum}
          </span>
        </div>
        <Button variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity" onClick={onAddWorkout}>
          <Plus className="h-3 w-3" />
        </Button>
      </div>
      <div className="space-y-1.5 flex-1">
        {workouts.map((w) => (
          <DraggableWorkout key={w.id} workout={w} onRemove={() => onRemoveWorkout(w.id)} />
        ))}
      </div>
      {workouts.length === 0 && (
        <div className="flex-1 flex items-center justify-center">
          <span className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground/40">Rest</span>
        </div>
      )}
    </div>
  );
}

export default function PlanCalendarPage() {
  const queryClient = useQueryClient();
  const { selectedPlanInstanceId } = useCoachStore();
  const { data: instancesData } = usePlanInstances();
  const planInstances = instancesData?.items ?? [];
  const selectedInstance = planInstances.find(pi => pi.id === selectedPlanInstanceId);

  const [weekOffset, setWeekOffset] = useState(0);
  const baseDate = new Date();
  baseDate.setDate(baseDate.getDate() + weekOffset * 7);
  const weekDates = getWeekDates(baseDate);
  const startDate = weekDates[0];
  const endDate = weekDates[6];

  const { data: calendarData, refetch: refetchCalendar } = usePlanCalendar(selectedInstance?.id ?? null, startDate, endDate);
  const calendarEntries: CalendarEntryResponse[] = calendarData?.entries ?? [];

  const { data: planTemplateData } = usePlanTemplate(selectedInstance?.plan_template_id ?? null);
  const planTemplate = planTemplateData;

  const { data: workoutsData } = useWorkoutTemplates();
  const workoutTemplates = workoutsData?.items ?? [];

  const addEntryMutation = useAddCalendarEntry(selectedInstance?.id ?? '');
  const deleteEntryMutation = useDeleteCalendarEntry(selectedInstance?.id ?? '');
  const updateEntryMutation = useUpdateCalendarEntry(selectedInstance?.id ?? '');

  const [activeWorkout, setActiveWorkout] = useState<ResolvedWorkout | null>(null);
  const [addingToDate, setAddingToDate] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  /** Group calendar entries by date and resolve template info */
  const resolveByDate = (date: string): ResolvedWorkout[] => {
    return calendarEntries
      .filter(e => e.scheduled_date === date)
      .map(e => ({
        id: e.id,
        workout_template_id: e.workout_template_id,
        name: e.template.name,
        sport: (e.template.sport as SportType) ?? 'running',
        stimulus: e.template.stimulus,
      }));
  };

  if (!selectedInstance) {
    return (
      <CoachLayout>
        <div className="rounded-lg border border-border/15 bg-card/20 p-12 text-center">
          <p className="text-sm text-muted-foreground">No plan instance selected. Go to Plan Builder to select a plan.</p>
        </div>
      </CoachLayout>
    );
  }

  const handleDragStart = (event: { active: { id: string | number } }) => {
    for (const date of weekDates) {
      const found = resolveByDate(date).find((w) => w.id === event.active.id);
      if (found) { setActiveWorkout(found); break; }
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveWorkout(null);
    const { active, over } = event;
    if (!over) return;

    const targetDate = String(over.id);
    if (!weekDates.includes(targetDate)) return;

    // Find source entry
    const sourceEntry = calendarEntries.find(e => e.id === String(active.id));
    if (!sourceEntry || sourceEntry.scheduled_date === targetDate) return;

    // Optimistic update — move the entry in cache immediately so UI doesn't snap back
    const calendarQueryKey = ['planCalendar', selectedInstance?.id, startDate, endDate];
    const previousData = queryClient.getQueryData(calendarQueryKey);
    queryClient.setQueryData(calendarQueryKey, (old: { entries: CalendarEntryResponse[]; total: number } | undefined) => {
      if (!old) return old;
      return {
        ...old,
        entries: old.entries.map(e =>
          e.id === sourceEntry.id ? { ...e, scheduled_date: targetDate } : e
        ),
      };
    });

    try {
      await updateEntryMutation.mutateAsync({
        workoutInstanceId: sourceEntry.id,
        payload: { scheduled_date: targetDate },
      });
      void refetchCalendar();
      toast.success('Workout moved');
    } catch {
      // Roll back on failure
      queryClient.setQueryData(calendarQueryKey, previousData);
      toast.error('Failed to move workout');
    }
  };

  const handleRemoveWorkout = async (workoutId: string) => {
    try {
      await deleteEntryMutation.mutateAsync(workoutId);
      void refetchCalendar();
      toast.success('Workout removed from calendar');
    } catch {
      toast.error('Failed to remove workout');
    }
  };

  const handleAddWorkoutToDay = async (date: string, workoutTemplateId: string) => {
    const template = workoutTemplates.find(t => t.id === workoutTemplateId);
    if (!template) return;
    try {
      await addEntryMutation.mutateAsync({ workout_template_id: workoutTemplateId, scheduled_date: date });
      void refetchCalendar();
      toast.success(`"${template.name}" added`);
      setAddingToDate(null);
    } catch {
      toast.error('Failed to add workout');
    }
  };

  return (
    <CoachLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-foreground">Plan Calendar</h1>
            <p className="text-xs text-muted-foreground mt-0.5">{planTemplate?.name ?? 'Plan'} · Week view</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" onClick={() => setWeekOffset(w => w - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-xs font-mono text-muted-foreground">{formatWeekLabel(weekDates)}</span>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" onClick={() => setWeekOffset(w => w + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Phase indicator */}
        {planTemplate?.phases[0] && (
          <div className="flex items-center gap-3">
            <div className="h-2 w-2 rounded-full" style={{ background: planTemplate.phases[0].color ?? 'hsl(200 65% 55%)' }} />
            <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
              {planTemplate.phases[0].name} Phase · W{planTemplate.phases[0].start_week}–W{planTemplate.phases[0].end_week}
            </span>
            <span className="text-[10px] text-muted-foreground/60">{planTemplate.phases[0].focus}</span>
          </div>
        )}

        {/* Calendar grid */}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="grid grid-cols-7 gap-2">
            {weekDates.map((date, i) => (
              <DroppableDay key={date} date={date} dayName={dayNames[i]}
                workouts={resolveByDate(date)}
                onRemoveWorkout={(id) => handleRemoveWorkout(id)}
                onAddWorkout={() => setAddingToDate(addingToDate === date ? null : date)} />
            ))}
          </div>
          <DragOverlay>
            {activeWorkout && (() => {
              const sport = sportConfig[activeWorkout.sport] ?? sportConfig.running;
              return (
                <div className="rounded-md border px-2.5 py-2 shadow-xl" style={{ borderColor: `${sport.color}40`, background: `${sport.color}15` }}>
                  <p className="text-[11px] font-medium text-foreground">{activeWorkout.name}</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <div className="h-1.5 w-1.5 rounded-full" style={{ background: sport.color }} />
                    <span className="text-[9px] text-muted-foreground">{sport.label}</span>
                  </div>
                </div>
              );
            })()}
          </DragOverlay>
        </DndContext>

        {/* Add workout picker */}
        {addingToDate && (
          <div className="rounded-lg border border-accent/20 bg-card/30 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                Add workout to {dayNames[weekDates.indexOf(addingToDate)]} {parseInt(addingToDate.split('-')[2], 10)}
              </p>
              <Button variant="ghost" size="sm" className="text-[10px] text-muted-foreground" onClick={() => setAddingToDate(null)}>Cancel</Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {workoutTemplates.map(t => {
                const sport = sportConfig[t.sport as SportType];
                return (
                  <button key={t.id} onClick={() => handleAddWorkoutToDay(addingToDate, t.id)}
                    className="text-left rounded-md border border-border/15 px-3 py-2 hover:bg-accent/5 hover:border-accent/30 transition-all">
                    <p className="text-[11px] text-foreground font-medium truncate">{t.name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {sport && <span className="text-[8px] font-mono uppercase" style={{ color: sport.color }}>{sport.label}</span>}
                      <span className="text-[9px] text-muted-foreground">{t.duration}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="flex flex-wrap gap-4">
          {Object.entries(sportConfig).map(([key, config]) => (
            <div key={key} className="flex items-center gap-1.5">
              <div className="h-2 w-2 rounded-full" style={{ background: config.color }} />
              <span className="text-[9px] font-mono text-muted-foreground">{config.label}</span>
            </div>
          ))}
        </div>
      </div>
    </CoachLayout>
  );
}
